import { Check, X } from 'lucide-react'

import { HeroKicker } from '@/components/marketing/hero-kicker'
import { Button } from '@/components/ui/button'
import type {
  PricingFeatureSection,
  PricingTier,
} from '@/config/marketing-content/pricing'
import { cn } from '@/lib/utils'

function PricingTierBadge({
  label,
  variant,
}: {
  label: string
  variant: PricingTier['badgeVariant']
}) {
  return (
    <HeroKicker
      className={cn(
        'pricing-tier-badge',
        variant === 'popular' && 'pricing-tier-badge--popular',
        variant === 'advanced' && 'pricing-tier-badge--advanced'
      )}
    >
      {label}
    </HeroKicker>
  )
}

function PricingFeatureList({ section }: { section: PricingFeatureSection }) {
  return (
    <div className="pricing-tier-section">
      <h3 className="pricing-tier-section__title">{section.title}</h3>
      <ul className="pricing-tier-section__list">
        {section.features.map((feature) => (
          <li
            key={feature.label}
            className={cn(
              'pricing-tier-feature',
              !feature.included && 'pricing-tier-feature--excluded'
            )}
          >
            {feature.included ? (
              <Check aria-hidden className="pricing-tier-feature__icon" />
            ) : (
              <X aria-hidden className="pricing-tier-feature__icon" />
            )}
            <span>{feature.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function PricingTierCard({
  tier,
  className,
  cardRef,
}: {
  tier: PricingTier
  className?: string
  cardRef?: (node: HTMLElement | null) => void
}) {
  return (
    <article
      ref={cardRef}
      className={cn(
        'pricing-tier-card',
        tier.highlighted && 'pricing-tier-card--highlighted',
        className
      )}
    >
      <div className="pricing-tier-card__header">
        <div className="pricing-tier-card__badge-slot">
          <PricingTierBadge label={tier.badge} variant={tier.badgeVariant} />
        </div>
        <div className="pricing-tier-card__intro">
          <h2 className="pricing-tier-card__name">{tier.name}</h2>
          <p className="pricing-tier-card__price">
            {tier.price}
            {tier.period ? (
              <span className="pricing-tier-card__period">{tier.period}</span>
            ) : null}
          </p>
        </div>
        <p className="pricing-tier-card__description">{tier.description}</p>
        <Button asChild variant="cta" size="cta">
          <a href={tier.ctaHref} className="pricing-tier-card__cta">
            {tier.ctaLabel}
          </a>
        </Button>
      </div>
      <div className="pricing-tier-card__sections">
        {tier.sections.map((section) => (
          <PricingFeatureList key={section.title} section={section} />
        ))}
      </div>
    </article>
  )
}
